import { useEffect, useMemo, useState } from "react";

async function api(path) {
  return fetch(path, { credentials: "same-origin" });
}

const KINDS = {
  event: { label: "Event", color: "#007a12" },
  meeting: { label: "Meeting", color: "#5466ae" },
  compliance: { label: "Compliance due", color: "#b3831d" },
  action: { label: "Action due", color: "#8a4baf" },
  dues: { label: "Dues renewal", color: "#b42318" },
  term: { label: "Term ends", color: "#2a7f7f" },
};

const iso = (d) => d.toISOString().slice(0, 10);

function gridRange(monthStart) {
  const first = new Date(monthStart);
  const start = new Date(first);
  start.setDate(1 - ((first.getDay() + 6) % 7)); // back to Monday
  const last = new Date(first.getFullYear(), first.getMonth() + 1, 0);
  const end = new Date(last);
  end.setDate(last.getDate() + ((7 - ((last.getDay() + 6) % 7) - 1) % 7)); // on to Sunday
  return [start, end];
}

export function Calendar({ chapterId }) {
  const [monthStart, setMonthStart] = useState(() => {
    const now = new Date();
    return new Date(now.getFullYear(), now.getMonth(), 1);
  });
  const [items, setItems] = useState(null);
  const [selected, setSelected] = useState(null);

  const [gridStart, gridEnd] = useMemo(() => gridRange(monthStart), [monthStart]);

  useEffect(() => {
    setItems(null);
    api(
      `/api/chapters/${chapterId}/calendar?from=${iso(gridStart)}&to=${iso(gridEnd)}`
    ).then(async (res) => {
      if (res.ok) setItems((await res.json()).items);
    });
  }, [chapterId, gridStart, gridEnd]);

  const byDay = useMemo(() => {
    const map = new Map();
    for (const item of items || []) {
      if (!map.has(item.date)) map.set(item.date, []);
      map.get(item.date).push(item);
    }
    return map;
  }, [items]);

  const days = useMemo(() => {
    const out = [];
    for (let d = new Date(gridStart); d <= gridEnd; d.setDate(d.getDate() + 1)) {
      out.push(new Date(d));
    }
    return out;
  }, [gridStart, gridEnd]);

  function shiftMonth(delta) {
    setSelected(null);
    setMonthStart(new Date(monthStart.getFullYear(), monthStart.getMonth() + delta, 1));
  }

  const today = iso(new Date());
  const monthLabel = monthStart.toLocaleDateString("en-GB", { month: "long", year: "numeric" });
  const selectedItems = selected ? byDay.get(selected) || [] : [];

  return (
    <>
      <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 12 }}>
        <button className="btn-ghost" type="button" onClick={() => shiftMonth(-1)}>
          ←
        </button>
        <strong style={{ fontSize: 17, minWidth: 150, textAlign: "center" }}>{monthLabel}</strong>
        <button className="btn-ghost" type="button" onClick={() => shiftMonth(1)}>
          →
        </button>
        <button
          className="btn-ghost"
          type="button"
          onClick={() => {
            const now = new Date();
            setMonthStart(new Date(now.getFullYear(), now.getMonth(), 1));
            setSelected(null);
          }}
        >
          Today
        </button>
        <span style={{ flex: 1 }} />
        {Object.entries(KINDS).map(([k, v]) => (
          <span key={k} className="muted" style={{ fontSize: 12, display: "inline-flex", alignItems: "center", gap: 4 }}>
            <span style={{ width: 8, height: 8, borderRadius: "50%", background: v.color, display: "inline-block" }} />
            {v.label}
          </span>
        ))}
      </div>

      {items === null ? (
        <p className="muted">Loading…</p>
      ) : (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", gap: 4 }}>
          {["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"].map((d) => (
            <div key={d} className="muted" style={{ fontSize: 12, fontWeight: 600, textAlign: "center", padding: "2px 0" }}>
              {d}
            </div>
          ))}
          {days.map((d) => {
            const key = iso(d);
            const dayItems = byDay.get(key) || [];
            const inMonth = d.getMonth() === monthStart.getMonth();
            return (
              <div
                key={key}
                onClick={() => setSelected(key === selected ? null : key)}
                style={{
                  background: key === selected ? "var(--green-tint)" : "var(--surface)",
                  border: `1px solid ${key === selected ? "var(--green-tint-line)" : "var(--line)"}`,
                  borderRadius: 8,
                  minHeight: 84,
                  padding: 6,
                  cursor: dayItems.length ? "pointer" : "default",
                  opacity: inMonth ? 1 : 0.45,
                }}
              >
                <div
                  style={{
                    fontSize: 12,
                    fontWeight: key === today ? 700 : 500,
                    color: key === today ? "var(--green)" : "var(--muted)",
                    marginBottom: 4,
                  }}
                >
                  {d.getDate()}
                </div>
                {dayItems.slice(0, 3).map((item, i) => (
                  <div
                    key={i}
                    style={{
                      fontSize: 11,
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      display: "flex",
                      alignItems: "center",
                      gap: 4,
                      marginBottom: 2,
                    }}
                    title={`${KINDS[item.kind].label}: ${item.title}`}
                  >
                    <span style={{ width: 7, height: 7, borderRadius: "50%", background: KINDS[item.kind].color, flex: "none" }} />
                    <span style={{ overflow: "hidden", textOverflow: "ellipsis" }}>{item.title}</span>
                  </div>
                ))}
                {dayItems.length > 3 && (
                  <div className="muted" style={{ fontSize: 11 }}>
                    +{dayItems.length - 3} more
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {selected && (
        <div className="form-card" style={{ marginTop: 14 }}>
          <div className="section-head" style={{ marginBottom: 8 }}>
            <h3>
              {new Date(selected + "T00:00:00").toLocaleDateString("en-GB", {
                weekday: "long",
                day: "numeric",
                month: "long",
              })}
            </h3>
          </div>
          {selectedItems.length ? (
            <table className="tbl">
              <tbody>
                {selectedItems.map((item, i) => (
                  <tr key={i}>
                    <td style={{ width: 130 }}>
                      <span
                        className="pill"
                        style={{ background: `${KINDS[item.kind].color}18`, color: KINDS[item.kind].color, borderColor: "transparent" }}
                      >
                        {KINDS[item.kind].label}
                      </span>
                    </td>
                    <td>{item.title}</td>
                    <td className="muted" style={{ fontSize: 13 }}>
                      {item.detail}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <p className="empty">Nothing on this day.</p>
          )}
        </div>
      )}
    </>
  );
}
